import React, { useState, useEffect } from "react";
import axios from "axios";
import { AuthGuard, EmailGuard } from "../App";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaPlus, FaEdit,FaTimesCircle, FaTimes, FaSave, FaUser, FaHome, FaCalendarAlt, FaCheckCircle, FaExclamationCircle, FaPhoneAlt, FaSpinner, FaUsers, FaBuilding } from 'react-icons/fa';
import { Oval } from "react-loader-spinner"; // Assuming this is available
import { StandardURL } from "../App";

// --- Helper Functions for UI ---

const getStatusBadge = (status, type = 'booking') => {
    const lowerStatus = status ? status.toLowerCase() : 'unknown';
    let colorClass = 'bg-gray-100 text-gray-800';
    let icon = <FaExclamationCircle className="w-3 h-3 mr-1" />;

    if (type === 'deposit') {
        switch (lowerStatus) {
            case 'true':
                colorClass = 'bg-green-100 text-green-800 border border-green-300';
                icon = <FaCheckCircle className="w-3 h-3 mr-1" />;
                break;
            case 'false':
                colorClass = 'bg-red-100 text-red-800 border border-red-300';
                icon = <FaTimesCircle className="w-3 h-3 mr-1" />;
                break;
        }
    } else { // Booking & Payment Status
        switch (lowerStatus) {
            case 'success':
                colorClass = 'bg-indigo-100 text-indigo-800 border border-indigo-300';
                icon = <FaCheckCircle className="w-3 h-3 mr-1" />;
                break;
            case 'pending':
                colorClass = 'bg-yellow-100 text-yellow-800 border border-yellow-300';
                icon = <FaExclamationCircle className="w-3 h-3 mr-1" />;
                break;
            case 'ended':
                colorClass = 'bg-gray-200 text-gray-600 border border-gray-400';
                icon = <FaTimes className="w-3 h-3 mr-1" />;
                break;
        }
    }

    return (
        <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full capitalize ${colorClass}`}>
            {icon} {status}
        </span>
    );
};

// --- Main Component ---

const ManualReservations = () => {
    const [reservations, setReservations] = useState([]);
    const [users, setUsers] = useState([]);
    const [properties, setProperties] = useState([]);
    const [isAdding, setIsAdding] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true); // Loading state for initial fetch
    const [editingReservation, setEditingReservation] = useState(null);

    const initialNewReservationState = {
        userid: "",
        propertyid: "",
        roomid: "", // Renamed to roomid to align with API/DB
        username: "",
        email: "",
        phone: "",
        entrydate: new Date(),
        housenumber: "",
        bookingstatus: "",
        depositpaid: "",
        paymentstatus: "",
    };
    
    const [newReservation, setNewReservation] = useState(initialNewReservationState);

    const token = localStorage.getItem("token");

    useEffect(() => {
        fetchReservations();
        fetchUsers();
        fetchProperties();
    }, []);

    const fetchReservations = async () => {
        setIsFetching(true);
        try {
            const response = await axios.get(
                `${StandardURL}/iNyumba/admin/manualreservations/reservation`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
             // Display newest first
            setReservations(Array.isArray(response.data.response) ? response.data.response.reverse() : []);
        } catch (error) {
            toast.error("Error fetching reservations");
            setReservations([]);
        } finally {
            setIsFetching(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await axios.get(
                `${StandardURL}/iNyumba/admin/manualreservations/users`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setUsers(Array.isArray(response.data.response) ? response.data.response : []);
        } catch (error) {
            toast.error("Error fetching users");
        }
    };

    const fetchProperties = async () => {
        try {
            const response = await axios.get(
                `${StandardURL}/iNyumba/admin/manualreservations/property`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Group rentals under their properties
            const propertiesData = response.data.response.reduce((acc, property) => {
                let existingProperty = acc.find(
                    (p) => p.property_id === property.property_id
                );
                if (!existingProperty) {
                    existingProperty = {
                        property_id: property.property_id,
                        title: property.title,
                        rentals: [],
                    };
                    acc.push(existingProperty);
                }
                if (property.rental_id) { // Only add if it's a rental unit
                     existingProperty.rentals.push({
                        rental_id: property.rental_id,
                        house_number: property.house_number,
                    });
                }
                return acc;
            }, []);
            setProperties(propertiesData);
        } catch (error) {
            toast.error("Error fetching properties");
        }
    };

    const resetModalState = () => {
        setNewReservation(initialNewReservationState);
        setIsAdding(false);
        setIsEditing(false);
        setEditingReservation(null);
    }

    const handleEditClick = (reservation) => {
        setEditingReservation(reservation);
        setNewReservation({
            userid: reservation.user_id,
            propertyid: reservation.property_id,
            roomid: reservation.rental_id,
            username: reservation.name || reservation.user_name,
            email: reservation.email,
            phone: reservation.phone,
            entrydate: new Date(reservation.entrydate),
            housenumber: reservation.house_number,
            bookingstatus: reservation.booking_status,
            depositpaid: reservation.deposit_paid,
            paymentstatus: reservation.payment_status,
        });
        setIsEditing(true);
    };

    const handleAddClick = () => {
        setNewReservation(initialNewReservationState);
        setIsAdding(true);
    };

    const handleUpdateReservation = async () => {
        if (!editingReservation) return;
        setIsLoading(true);

        const updatedFields = {
            phone: newReservation.phone,
            bookingstatus: newReservation.bookingstatus,
            paymentstatus: newReservation.paymentstatus,
            depositpaid: newReservation.depositpaid,
        };

        try {
            await axios.patch(
                `${StandardURL}/iNyumba/admin/manualreservations/update/${editingReservation.reservations_id}`,
                updatedFields,
                { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
            );
            toast.success("Reservation updated successfully!");
            resetModalState();
            fetchReservations();
        } catch (error) {
            toast.error(error.response?.data?.response || "Error updating reservation");
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddReservation = async () => {
        // Basic validation (can be expanded)
        if (!newReservation.userid || !newReservation.propertyid || !newReservation.roomid || !newReservation.bookingstatus || !newReservation.depositpaid || !newReservation.paymentstatus) {
            toast.error("Please fill in all required fields.");
            return;
        }

        setIsLoading(true);
        try {
            await axios.post(
                `${StandardURL}/iNyumba/admin/manualreservations/post`,
                {
                    ...newReservation,
                    entrydate: newReservation.entrydate.toISOString().split("T")[0],
                },
                { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
            );
            toast.success("Reservation added successfully!");
            resetModalState();
            fetchReservations();
        } catch (error) {
            toast.error(error.response?.data?.response || "Error adding reservation");
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewReservation((prev) => ({ ...prev, [name]: value }));
    };

    const handleUserSelect = (e) => {
        const userId = e.target.value;
        const selectedUser = users.find((user) => user.uuid === userId);
        setNewReservation((prev) => ({
            ...prev,
            userid: userId,
            username: selectedUser?.name || "",
            email: selectedUser?.email || "",
            phone: selectedUser?.phone || "",
        }));
    };

    const handlePropertySelect = (e) => {
        const propertyId = e.target.value;
        
        // Reset rental fields when property changes
        setNewReservation((prev) => ({
            ...prev,
            propertyid: propertyId,
            roomid: "", 
            housenumber: "", 
        }));
    };

    const handleRentalSelect = (e) => {
        const rentalId = e.target.value;
        const selectedRental = properties
            .find((property) => property.property_id === newReservation.propertyid)
            ?.rentals.find((rental) => rental.rental_id === rentalId);

        if (selectedRental) {
            setNewReservation((prev) => ({
                ...prev,
                roomid: rentalId,
                housenumber: selectedRental.house_number,
            }));
        }
    };

    // --- Render Logic ---

    if (isFetching && reservations.length === 0) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <Oval color="#4f46e5" secondaryColor="#a5b4fc" height={50} width={50} ariaLabel="loading-spinner" />
                <p className="ml-4 text-lg text-gray-600">Loading reservations...</p>
            </div>
        );
    }


    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
                    <div className="max-w-7xl mx-auto">
                        {/* Header and Add Button */}
                        <div className="flex justify-between items-center mb-8">
                            <h1 className="text-3xl font-extrabold text-gray-900 flex items-center">
                                <FaCalendarAlt className="mr-3 text-indigo-600" /> Manual Reservations
                            </h1>
                            <button
                                onClick={handleAddClick}
                                className="inline-flex items-center bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg transition duration-150 shadow-md"
                            >
                                <FaPlus className="mr-2" /> Add New Reservation
                            </button>
                        </div>
                        
                        {/* Reservation Table Card */}
                        <div className="bg-white shadow-xl rounded-xl overflow-hidden border-t-4 border-indigo-500">
                            
                            {reservations.length === 0 && !isFetching ? (
                                <div className="text-center p-12">
                                    <FaCalendarAlt className="mx-auto h-12 w-12 text-gray-400" />
                                    <h3 className="mt-2 text-lg font-medium text-gray-900">No Manual Reservations</h3>
                                    <p className="mt-1 text-sm text-gray-500">
                                        Use the button above to manually add a new reservation.
                                    </p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-100">
                                            <tr>
                                                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">User/Email</th>
                                                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">House No.</th>
                                                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Entry Date</th>
                                                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Booking Status</th>
                                                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Payment Status</th>
                                                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Deposit Paid</th>
                                                <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {reservations.map((reservation) => (
                                                <tr key={reservation.reservations_id} className="hover:bg-gray-50 transition duration-100">
                                                    <td className="py-3 px-4 text-sm font-medium text-gray-900">
                                                        <div className="font-bold">{reservation.user_name}</div>
                                                        <div className="text-gray-500 text-xs">{reservation.email}</div>
                                                    </td>
                                                    <td className="py-3 px-4 text-sm text-gray-700 font-semibold">{reservation.house_no}</td>
                                                    <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{reservation.entrydate}</td>
                                                    <td className="py-3 px-4 whitespace-nowrap">
                                                        {getStatusBadge(reservation.booking_status, 'booking')}
                                                    </td>
                                                    <td className="py-3 px-4 whitespace-nowrap">
                                                        {getStatusBadge(reservation.payment_status, 'payment')}
                                                    </td>
                                                    <td className="py-3 px-4 whitespace-nowrap">
                                                        {getStatusBadge(reservation.deposit_paid, 'deposit')}
                                                    </td>
                                                    <td className="py-3 px-4 whitespace-nowrap text-sm font-medium">
                                                        <button
                                                            onClick={() => handleEditClick(reservation)}
                                                            className="inline-flex items-center bg-green-600 hover:bg-green-700 text-white font-bold py-1.5 px-3 rounded-md transition duration-150 shadow-sm text-xs"
                                                        >
                                                            <FaEdit className="mr-1" /> Edit
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* --- Modals --- */}
                {/* Add Reservation Modal */}
                {(isAdding || isEditing) && (
                    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            {/* Background overlay */}
                            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={resetModalState}></div>

                            {/* Modal panel */}
                            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                            
                            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <h2 className="text-xl font-bold text-gray-900 mb-6 border-b pb-2">
                                        {isAdding ? "Add New Manual Reservation" : "Edit Reservation"}
                                    </h2>
                                    <form className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        
                                        {/* User Selection (Add Only) */}
                                        {isAdding && (
                                            <>
                                                <div className="col-span-2">
                                                    <label htmlFor="userid" className="block text-sm font-medium text-gray-700 flex items-center mb-1"><FaUsers className="mr-2" /> Select User</label>
                                                    <select
                                                        id="userid"
                                                        name="userid"
                                                        value={newReservation.userid}
                                                        onChange={handleUserSelect}
                                                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                        required
                                                    >
                                                        <option value="">-- Select User --</option>
                                                        {users.map((user) => (
                                                            <option key={user.uuid} value={user.uuid}>
                                                                {user.name} ({user.email})
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <InputGroup icon={FaUser} label="Username" value={newReservation.username} readOnly />
                                                <InputGroup icon={FaPhoneAlt} label="Phone" value={newReservation.phone} readOnly />
                                                
                                                <div className="col-span-2 border-t pt-4">
                                                    <label htmlFor="propertyid" className="block text-sm font-medium text-gray-700 flex items-center mb-1"><FaBuilding className="mr-2" /> Select Property</label>
                                                    <select
                                                        id="propertyid"
                                                        name="propertyid"
                                                        value={newReservation.propertyid}
                                                        onChange={handlePropertySelect}
                                                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                        required
                                                    >
                                                        <option value="">-- Select Property --</option>
                                                        {properties.map((property) => (
                                                            <option key={property.property_id} value={property.property_id}>
                                                                {property.title}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {newReservation.propertyid && (
                                                    <>
                                                        <div className="col-span-1">
                                                            <label htmlFor="rentalid" className="block text-sm font-medium text-gray-700 flex items-center mb-1"><FaHome className="mr-2" /> Select Unit</label>
                                                            <select
                                                                id="rentalid"
                                                                name="rentalid"
                                                                value={newReservation.roomid}
                                                                onChange={handleRentalSelect}
                                                                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                                required
                                                            >
                                                                <option value="">-- Select Unit --</option>
                                                                {properties
                                                                    .find((p) => p.property_id === newReservation.propertyid)
                                                                    ?.rentals.map((rental) => (
                                                                        <option key={rental.rental_id} value={rental.rental_id}>
                                                                            House No: {rental.house_number} (ID: {rental.rental_id})
                                                                        </option>
                                                                    ))}
                                                            </select>
                                                        </div>
                                                        <InputGroup icon={FaHome} label="House Number (Auto-filled)" value={newReservation.housenumber} readOnly />
                                                    </>
                                                )}

                                                <div className="col-span-1">
                                                    <label className="block text-sm font-medium text-gray-700 flex items-center mb-1"><FaCalendarAlt className="mr-2" /> Entry Date</label>
                                                    <DatePicker
                                                        selected={newReservation.entrydate}
                                                        onChange={(date) => setNewReservation({ ...newReservation, entrydate: date })}
                                                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                        dateFormat="yyyy/MM/dd"
                                                        required
                                                    />
                                                </div>
                                            </>
                                        )}
                                        
                                        {/* Edit Fields (Editable in both, Phone in Edit only) */}
                                        <div className={isEditing ? "col-span-1" : "col-span-1"}>
                                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 flex items-center mb-1"><FaPhoneAlt className="mr-2" /> Phone</label>
                                            <input
                                                type="text"
                                                id="phone"
                                                name="phone"
                                                placeholder="Phone Number"
                                                value={newReservation.phone}
                                                onChange={handleInputChange}
                                                readOnly={isAdding} // Read-only in Add mode, editable in Edit mode
                                                className={`block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm ${isAdding ? 'bg-gray-100 text-gray-600' : 'focus:ring-indigo-500 focus:border-indigo-500'}`}
                                            />
                                        </div>

                                        <SelectGroup 
                                            icon={FaCheckCircle} 
                                            label="Booking Status" 
                                            name="bookingstatus" 
                                            value={newReservation.bookingstatus} 
                                            onChange={handleInputChange}
                                            options={["SUCCESS", "PENDING", "ENDED"]}
                                        />

                                        <SelectGroup 
                                            icon={FaCalendarAlt} 
                                            label="Payment Status" 
                                            name="paymentstatus" 
                                            value={newReservation.paymentstatus} 
                                            onChange={handleInputChange}
                                            options={["SUCCESS", "PENDING"]}
                                        />

                                        <SelectGroup 
                                            icon={FaExclamationCircle} 
                                            label="Deposit Paid" 
                                            name="depositpaid" 
                                            value={newReservation.depositpaid} 
                                            onChange={handleInputChange}
                                            options={["TRUE", "FALSE"]}
                                        />
                                        
                                    </form>
                                </div>
                                
                                {/* Modal Footer */}
                                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                                    <button
                                        type="button"
                                        onClick={isAdding ? handleAddReservation : handleUpdateReservation}
                                        disabled={isLoading}
                                        className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-6 py-3 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm transition duration-150 ${
                                            isLoading ? "bg-indigo-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        }`}
                                    >
                                        {isLoading ? (
                                            <Oval height={20} width={20} color="white" visible={true} ariaLabel="loading-indicator" secondaryColor="#a5b4fc" />
                                        ) : (
                                            <>
                                                <FaSave className="mr-2 mt-0.5" /> {isAdding ? "Add Reservation" : "Save Changes"}
                                            </>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={resetModalState}
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-6 py-3 bg-white text-base font-medium text-gray-700 hover:bg-gray-100 sm:mt-0 sm:w-auto sm:text-sm"
                                    >
                                        <FaTimes className="mr-2 mt-0.5" /> Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </AuthGuard>
        </EmailGuard>
    );
};

// --- Reusable Input and Select Components for Modals ---

const InputGroup = ({ icon: Icon, label, value, name, onChange, readOnly = false }) => (
    <div className="col-span-1">
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 flex items-center mb-1"><Icon className="mr-2" /> {label}</label>
        <input
            type="text"
            id={name}
            name={name}
            value={value}
            onChange={onChange}
            readOnly={readOnly}
            placeholder={label}
            className={`block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm ${readOnly ? 'bg-gray-100 text-gray-600' : 'focus:ring-indigo-500 focus:border-indigo-500'}`}
        />
    </div>
);

const SelectGroup = ({ icon: Icon, label, name, value, onChange, options }) => (
    <div className="col-span-1">
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 flex items-center mb-1"><Icon className="mr-2" /> {label}</label>
        <select
            id={name}
            name={name}
            value={value}
            onChange={onChange}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            required
        >
            <option value="">-- Select {label} --</option>
            {options.map((option) => (
                <option key={option} value={option}>
                    {option}
                </option>
            ))}
        </select>
    </div>
);

export default ManualReservations;